import React from "react";
import styled, { css } from "styled-components";

type Size = "L" | "Q" | "M" | "S";

type CellGridProps = {
  size: Size;
  bordered?: boolean;
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
};

const configs: Record<Size, { rows: string[]; cols: number[] }> = {
  Q: { rows: "ABCDEF".split(""), cols: [8, 7, 6, 5, 4, 3, 2, 1] },
  L: { rows: "ABCDEFGH".split(""), cols: [6, 5, 4, 3, 2, 1] },
  M: { rows: "ABCDEFGH".split(""), cols: [4, 3, 2, 1] },
  S: { rows: "ABCDEF".split(""), cols: [4, 3, 2, 1] },
};

// --- Styled Components ---

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: sans-serif;
`;

const Title = styled.p`
  font-size: 16px;
  color: #333;
  margin-bottom: 16px;
`;

const GridContainer = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 48px);
  gap: 2px;
`;

const Cell = styled.button<{
  variant: "border" | "inner";
  selected: boolean;
}>`
  width: 48px;
  height: 32px;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === "border" &&
    css`
      background-color: #7f8fa6;
      color: white;
      ${props.selected &&
      css`
        background-color: #b91c1c;
      `}
      &:hover {
        background-color: ${props.selected ? "#d32f2f" : "#353b48"};
      }
    `}

  ${(props) =>
    props.variant === "inner" &&
    css`
      background-color: ${props.selected ? "#B91C1C" : "#f1f2f6"};
      color: ${props.selected ? "white" : "#353b48"};

      &:hover {
        background-color: ${props.selected ? "#d32f2f" : "#e5e6e8"};
      }
    `}
`;

const NotOnCellWrapper = styled.label`
  margin-top: 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  cursor: pointer;

  input {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

// --- Component ---

export const CellGrid: React.FC<CellGridProps> = ({
  size,
  bordered = true,
  selected,
  onSelectionChange,
}) => {
  const { rows, cols } = configs[size];
  const selectedSet = new Set(selected);

  const handleToggleCell = (id: string) => {
    const newSelection = new Set(selectedSet);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(Array.from(newSelection));
  };

  const handleNotOnCellToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = "NotOnCell";
    const newSelection = new Set(selectedSet);
    if (e.target.checked) {
      newSelection.add(id);
    } else {
      newSelection.delete(id);
    }
    onSelectionChange?.(Array.from(newSelection));
  };

  const gridColumns = bordered ? cols.length + 2 : cols.length;

  return (
    <Wrapper>
      <Title>Select the defect cells:</Title>

      <GridContainer columns={gridColumns}>
        {bordered && (
          <>
            <Cell
              variant="border"
              selected={selectedSet.has("TL")}
              onClick={() => handleToggleCell("TL")}
            >
              TL
            </Cell>
            {cols.map((c) => {
              const id = `T${c}`;
              return (
                <Cell
                  key={id}
                  variant="border"
                  selected={selectedSet.has(id)}
                  onClick={() => handleToggleCell(id)}
                >
                  {id}
                </Cell>
              );
            })}
            <Cell
              variant="border"
              selected={selectedSet.has("TR")}
              onClick={() => handleToggleCell("TR")}
            >
              TR
            </Cell>
          </>
        )}

        {rows.map((r) => (
          <React.Fragment key={r}>
            {bordered && (
              <Cell
                variant="border"
                selected={selectedSet.has(`${r}L`)}
                onClick={() => handleToggleCell(`${r}L`)}
              >
                {`${r}L`}
              </Cell>
            )}

            {cols.map((c) => {
              const id = `${r}${c}`;
              return (
                <Cell
                  key={id}
                  variant="inner"
                  selected={selectedSet.has(id)}
                  onClick={() => handleToggleCell(id)}
                >
                  {id}
                </Cell>
              );
            })}

            {bordered && (
              <Cell
                variant="border"
                selected={selectedSet.has(`${r}R`)}
                onClick={() => handleToggleCell(`${r}R`)}
              >
                {`${r}R`}
              </Cell>
            )}
          </React.Fragment>
        ))}

        {bordered && (
          <>
            <Cell
              variant="border"
              selected={selectedSet.has("JL")}
              onClick={() => handleToggleCell("JL")}
            >
              JL
            </Cell>
            {cols.map((c) => {
              const id = `J${c}`;
              return (
                <Cell
                  key={id}
                  variant="border"
                  selected={selectedSet.has(id)}
                  onClick={() => handleToggleCell(id)}
                >
                  {id}
                </Cell>
              );
            })}
            <Cell
              variant="border"
              selected={selectedSet.has("JR")}
              onClick={() => handleToggleCell("JR")}
            >
              JR
            </Cell>
          </>
        )}
      </GridContainer>

      <NotOnCellWrapper>
        <input
          type="checkbox"
          checked={selectedSet.has("NotOnCell")}
          onChange={handleNotOnCellToggle}
        />
        <span>Not on cell</span>
      </NotOnCellWrapper>
    </Wrapper>
  );
};
